import { type ComponentProps, type FC, useMemo } from "react";

import { Checkbox, Label } from "../../components";
import { cn } from "../../utils";
import { FieldBase, type FieldBaseProps } from "./field-base";
import { useField } from "./use-field";

interface FieldCheckboxPropsBase extends FieldBaseProps {
  value?: string;
  multiple?: boolean;
  onBlur?: ComponentProps<typeof Checkbox>["onBlur"];
  inputProps?: ComponentProps<typeof Checkbox>;
}

export interface FieldCheckboxPropsMultiple extends FieldCheckboxPropsBase {
  multiple: true;
  onChange?: (value: string[]) => void;
}

export interface FieldCheckboxPropsSingle extends FieldCheckboxPropsBase {
  multiple?: false;
  onChange?: (checked: boolean) => void;
}

export type FieldCheckboxProps = FieldCheckboxPropsMultiple | FieldCheckboxPropsSingle;

export const FieldCheckbox: FC<FieldCheckboxProps> = ({
  disabled,
  label,
  value,
  multiple,
  onChange,
  onBlur,
  inputProps,
  readOnly,
  ...props
}) => {
  const { field, hasError } = useField({ name: props.name, disabled: disabled || readOnly, inputProps });

  const isChecked = useMemo(() => {
    if (inputProps?.checked !== undefined) {
      return inputProps.checked;
    }

    return multiple ? field.value?.includes(value) : !!field.value;
  }, [inputProps?.checked, field.value, value, multiple]);

  const handleChangeMultiple = () => {
    const values = field.value?.includes(value)
      ? field.value?.filter((v: string) => v !== value)
      : [...(field.value || []), value];

    field.onChange(values);
    (onChange as FieldCheckboxPropsMultiple["onChange"])?.(values);
  };

  const handleChangeSingle: FieldCheckboxPropsSingle["onChange"] = (checked: boolean) => {
    field.onChange(checked);
    (onChange as FieldCheckboxPropsSingle["onChange"])?.(checked);
  };

  const handleChange = multiple ? handleChangeMultiple : handleChangeSingle;

  const handleBlur: FieldCheckboxProps["onBlur"] = (event) => {
    field.onBlur();
    onBlur?.(event);
  };

  return (
    <FieldBase labelOrientation="horizontal" labelPosition="end" {...props}>
      <Label className="flex flex-auto items-start gap-1.5 text-sm font-normal group-data-[disabled]/form-field:cursor-not-allowed group-data-[disabled]/form-field:pointer-events-none group-data-[disabled]/form-field:opacity-70">
        <Checkbox
          {...field}
          {...inputProps}
          value={value}
          checked={isChecked}
          onCheckedChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled || readOnly}
          className={cn({ "border border-destructive": hasError && !field.disabled }, inputProps?.className)}
        />
        {label}
      </Label>
    </FieldBase>
  );
};
